import { ApplicationForm } from '@/types/forms/specific/applicationForm';
import { BillingFormStep } from '@/types/forms/formSteps/billingFormStep';
import { getOverallNominalPower } from '../../utilities/application/plantPerformance';
import { isMediumVoltage } from './common';

export const billingRules = async (
    formStep: { model: BillingFormStep; formModel: ApplicationForm } & Vue,
): Promise<void> => {
    const applicationType = formStep.formModel.applicationType;

    const nominalPower =
        getOverallNominalPower(formStep.formModel.plantPerformance) ?? 0;

    const noPvOrDeaWithNominalPowerLte100AndNotMv =
        (applicationType !== 'photovoltaic' &&
            applicationType !== 'photovoltaicAndStorageLte30' &&
            applicationType !== 'dea') ||
        nominalPower > 100 ||
        isMediumVoltage(formStep.formModel.powerConnection);

    // Enable all options first, then disable the ones that are not allowed based on the current form state
    formStep.model.plantSalesMethodAssignment.options.forEach(
        (o) => (o.disabled = false),
    );

    formStep.model.plantSalesMethodAssignment.disabled = noPvOrDeaWithNominalPowerLte100AndNotMv;

    if (
        !formStep.formModel.plantData.pvInstallationType.disabled &&
        formStep.formModel.plantData.pvInstallationType.value ===
            'notEEGCompliant' &&
        !formStep.model.plantSalesMethodAssignment.disabled
    ) {
        formStep.model.plantSalesMethodAssignment.options.forEach((option) => {
            switch (option.key) {
                case 'statutoryCompensation':
                    option.disabled = true;
                    break;
                case 'directMarketingMarketPremium':
                    option.disabled = true;
                    break;
                case 'otherDirectMarketing':
                    option.disabled = false;
                    break;
                case 'freeAcceptance':
                    option.disabled = false;
                    break;
            }
        });
    } else {
        formStep.model.plantSalesMethodAssignment.options.forEach((option) => {
            option.disabled = false;
        });
    }

    // https://netzkern.atlassian.net/wiki/spaces/EWE/pages/4868800911/Formular+Abrechnung#FormularAbrechnung-noEegRemuneration
    formStep.model.noEegRemuneration.disabled =
        (applicationType !== 'photovoltaic' &&
            applicationType !== 'photovoltaicAndStorageLte30' &&
            applicationType !== 'dea') ||
        (!formStep.formModel.generationType
            .proceedWithFullRegistrationForEEGCompensation.disabled &&
            !!formStep.formModel.generationType
                .proceedWithFullRegistrationForEEGCompensation.value);

    // https://netzkern.atlassian.net/wiki/spaces/EWE/pages/4868800911/Formular+Abrechnung#FormularAbrechnung-reasonNoEegRemuneration
    formStep.model.reasonNoEegRemuneration.disabled =
        formStep.model.noEegRemuneration.disabled ||
        !formStep.model.noEegRemuneration.value;

    // https://netzkern.atlassian.net/wiki/spaces/EWE/pages/4868800911/Formular+Abrechnung#FormularAbrechnung-explanationNoEegRemuneration
    formStep.model.explanationNoEegRemuneration.disabled =
        formStep.model.reasonNoEegRemuneration.disabled ||
        formStep.model.reasonNoEegRemuneration.value !== 'other';

    // https://netzkern.atlassian.net/wiki/spaces/EWE/pages/4868800911/Formular+Abrechnung#FormularAbrechnung-noEegRemunerationHint
    formStep.model.noEegRemunerationHint.disabled =
        formStep.model.noEegRemuneration.disabled ||
        !formStep.model.noEegRemuneration.value;

    // https://netzkern.atlassian.net/wiki/spaces/EWE/pages/4854573897/Formular+Abrechnung#FormularAbrechnung-taxNumber
    formStep.model.taxNumber.disabled =
        (!formStep.model.taxNumberWillFollow.disabled &&
            !!formStep.model.taxNumberWillFollow.value) ||
        formStep.model.tax.disabled ||
        formStep.model.tax.value === 'noSalesTax';

    formStep.model.smallPVTaxAdvisorHint.disabled =
        nominalPower > 30 ||
        formStep.formModel.powerConnection.hasExistingPlants.value ||
        formStep.model.tax.value === 'noSalesTax';

    formStep.model.matchingTaxIDHint.disabled = !formStep.formModel
        .powerConnection.hasExistingPlants.value;

    // https://netzkern.atlassian.net/wiki/spaces/EWE/pages/4854573897/Formular+Abrechnung#FormularAbrechnung-taxNumberWillFollow
    formStep.model.taxNumberWillFollow.disabled =
        formStep.model.tax.disabled ||
        formStep.model.tax.value === 'noSalesTax';

    formStep.model.noFinancialDistress.disabled = noPvOrDeaWithNominalPowerLte100AndNotMv;

    formStep.model.noPendingEURecoveryClaims.disabled = noPvOrDeaWithNominalPowerLte100AndNotMv;

    const noEegRenumerationIsChecked =
        !formStep.model.noEegRemuneration.disabled &&
        !!formStep.model.noEegRemuneration.value;

    // https://netzkern.atlassian.net/wiki/spaces/EWE/pages/4854573897/Formular+Abrechnung#FormularAbrechnung-directDebitMandate
    formStep.model.directDebitMandate.disabled = noEegRenumerationIsChecked;

    // https://netzkern.atlassian.net/wiki/spaces/EWE/pages/4854573897/Formular+Abrechnung#FormularAbrechnung-iban
    formStep.model.iban.disabled = noEegRenumerationIsChecked;

    // https://netzkern.atlassian.net/wiki/spaces/EWE/pages/4854573897/Formular+Abrechnung#FormularAbrechnung-nameAndDomicileCreditInstitution
    formStep.model.nameAndDomicileCreditInstitution.disabled = noEegRenumerationIsChecked;

    // https://netzkern.atlassian.net/wiki/spaces/EWE/pages/4854573897/Formular+Abrechnung#FormularAbrechnung-date
    formStep.model.date.disabled = noEegRenumerationIsChecked;

    // https://netzkern.atlassian.net/wiki/spaces/EWE/pages/4854573897/Formular+Abrechnung#FormularAbrechnung-accountOwnerName
    formStep.model.accountOwnerName.disabled = noEegRenumerationIsChecked;
};
